import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Breadcrumb } from "@/components/Breadcrumb";
import { CTASection } from "@/components/CTASection";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import heroImage from "@/assets/brick-work-masonry-stairs-wayne-nj-hero.webp";
import img1 from "@/assets/masonry-brick-steps-north-haledon-nj.webp";
import img2 from "@/assets/breezewood-retaining-wall-installation-paramus-nj.webp";
import img3 from "@/assets/masonry-construction-totowa-nj.webp";
import img4 from "@/assets/concrete-driveway-installation-kinnelon-nj.webp";
import img5 from "@/assets/customerr-masonry-paver-patio-installation-nutley-nj.webp";

const BASE_URL = "https://masonry-pro.com";

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "How to Choose the Right Masonry Contractor in North Jersey",
  image: `${BASE_URL}/assets/brick-work-masonry-stairs-wayne-nj-hero.webp`,
  datePublished: "2026-03-01",
  dateModified: "2026-03-19",
  author: {
    "@type": "Organization",
    name: "Masonry Pro Construction",
    url: BASE_URL,
  },
  publisher: {
    "@type": "Organization",
    name: "Masonry Pro Construction",
    url: BASE_URL,
  },
  description:
    "What North Jersey homeowners should look for when hiring a masonry contractor. NJ registration requirements, insurance, union training, and how to spot the difference between quality work and a shortcut.",
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "How do I check if a masonry contractor is registered in New Jersey?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "You can verify a contractor's registration through the New Jersey Division of Consumer Affairs. Every registered contractor has an NJHIC number, and it should appear on their estimates, contracts, and vehicles. If a contractor can not provide this number, they may not be legally permitted to do home improvement work in the state.",
      },
    },
    {
      "@type": "Question",
      name: "What insurance should a masonry contractor have in NJ?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "At minimum, they should carry workers compensation insurance and commercial general liability coverage of at least $500,000 per occurrence. The updated NJ contractor registration law also requires a compliance bond. Ask for a copy of their insurance certificate before any work starts.",
      },
    },
    {
      "@type": "Question",
      name: "How much should I pay up front for a masonry project?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "New Jersey law requires any home improvement contract over $500 to be in writing, and the deposit should be reasonable. Be cautious of contractors who ask for a large percentage of the project cost before starting work. A written estimate with a payment schedule tied to project milestones is a better arrangement for both sides.",
      },
    },
    {
      "@type": "Question",
      name: "Do masonry contractors need a specific license in New Jersey?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "As of January 2024, New Jersey signed the Home Improvement and Home Elevation Contractor Licensing Act into law, which created a new state licensing board and added stricter requirements for contractors. The licensing provisions are being phased in, but all contractors are already required to be registered with the Division of Consumer Affairs and carry the required insurance and bonding.",
      },
    },
    {
      "@type": "Question",
      name: "What makes masonry work different in North Jersey compared to other areas?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "The clay-heavy glacial soils, the high number of freeze-thaw cycles each winter, and the fact that every municipality has its own permit rules all make masonry work more demanding here. A contractor who understands these conditions will build deeper bases, plan for drainage, and know which building department to call in each town.",
      },
    },
  ],
};

const faqs = [
  {
    q: "How do I check if a masonry contractor is registered in New Jersey?",
    a: "You can verify a contractor's registration through the New Jersey Division of Consumer Affairs.¹ Every registered contractor has an NJHIC number, and it should appear on their estimates, contracts, and vehicles. If a contractor can not provide this number, they may not be legally permitted to do home improvement work in the state.",
  },
  {
    q: "What insurance should a masonry contractor have in NJ?",
    a: "At minimum, they should carry workers compensation insurance and commercial general liability coverage of at least $500,000 per occurrence.³ The updated NJ contractor registration law also requires a compliance bond. Ask for a copy of their insurance certificate before any work starts.",
  },
  {
    q: "How much should I pay up front for a masonry project?",
    a: "New Jersey law requires any home improvement contract over $500 to be in writing, and the deposit should be reasonable. Be cautious of contractors who ask for a large percentage of the project cost before starting work. A written estimate with a payment schedule tied to project milestones is a better arrangement for both sides.",
  },
  {
    q: "Do masonry contractors need a specific license in New Jersey?",
    a: "As of January 2024, New Jersey signed the Home Improvement and Home Elevation Contractor Licensing Act into law, which created a new state licensing board and added stricter requirements for contractors.² The licensing provisions are being phased in, but all contractors are already required to be registered with the Division of Consumer Affairs and carry the required insurance and bonding.",
  },
  {
    q: "What makes masonry work different in North Jersey compared to other areas?",
    a: "The clay-heavy glacial soils, the high number of freeze-thaw cycles each winter, and the fact that every municipality has its own permit rules all make masonry work more demanding here. A contractor who understands these conditions will build deeper bases, plan for drainage, and know which building department to call in each town. Someone without that local experience may not account for any of it.",
  },
];

const citations = [
  "NJ Contractors Registration Act (N.J.S.A. 56:8-136 et seq.) — NJBIA Overview — Registration requirements for home improvement contractors in New Jersey",
  "P.L. 2023, c. 237 — Home Improvement and Home Elevation Contractor Licensing Act — Cole Schotz Analysis — Overview of the January 2024 law creating the NJ State Board of Home Improvement Contractors",
  "NJ Division of Consumer Affairs — Notice to Home Improvement Contractors Regarding Changes to the Law — Updated insurance, bonding, and registration requirements effective 2024-2025",
];

/* Alternating image + text row */
function ContentImageRow({
  image,
  alt,
  children,
  reverse = false,
}: {
  image: string;
  alt: string;
  children: React.ReactNode;
  reverse?: boolean;
}) {
  return (
    <div
      className={`flex flex-col ${reverse ? "md:flex-row-reverse" : "md:flex-row"} gap-8 md:gap-12 items-center`}
    >
      <div className="w-full md:w-1/2">
        <img
          src={image}
          alt={alt}
          loading="lazy"
          width={800}
          height={600}
          className="rounded-xl w-full h-auto object-cover shadow-lg"
        />
      </div>
      <div className="w-full md:w-1/2 prose prose-lg prose-invert max-w-none">
        {children}
      </div>
    </div>
  );
}

export default function ChoosingProfessionalMasonryCompany() {
  return (
    <>
      <Helmet>
        <title>Choosing a Masonry Contractor in North Jersey | Masonry Pro</title>
        <meta name="description" content="What North Jersey homeowners should look for when hiring a masonry contractor. NJ registration, insurance, union training, and how to spot the difference." />
        <meta property="og:description" content="What North Jersey homeowners should look for when hiring a masonry contractor. NJ registration, insurance, union training, and how to spot the difference." />
        <meta property="og:title" content="Choosing a Masonry Contractor in North Jersey | Masonry Pro" />
        <link rel="preload" as="image" href={heroImage} />
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <meta property="og:image" content="https://masonry-pro.com/og/services.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
      </Helmet>
      <Header />
      <Breadcrumb />

      <main className="bg-background">
        {/* Hero */}
        <section className="relative py-20 md:py-28">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${heroImage})` }}
          >
            <div className="absolute inset-0 bg-background/80" />
          </div>
          <div className="container mx-auto px-4 relative z-10 text-center max-w-3xl">
            <p className="text-secondary font-semibold mb-3 uppercase tracking-wider text-sm">
              Pro Tips
            </p>
            <h1 className="text-3xl md:text-5xl font-bold mb-4">
              How to Choose the Right{" "}
              <span className="text-gradient-gold">Masonry Contractor</span> in North Jersey
            </h1>
          </div>
        </section>

        {/* Intro */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4 max-w-3xl prose prose-lg prose-invert">
            <p>
              Finding a masonry contractor in North Jersey is not hard. However, finding one who is registered, insured, and actually knows how to build for the soil and weather conditions here is a different story. In North New Jersey, Bergen, Passaic, Essex, and Morris Counties all have clay-heavy glacial soils that can shift with the seasons, which can quickly expose poor craftsmanship.
            </p>
            <p>
              This page covers what to look for when hiring a mason, what New Jersey law requires from contractors, and what separates a job that holds up from one that falls apart in a few years.
            </p>
          </div>
        </section>

        {/* Content Rows */}
        <section className="pb-16 md:pb-24">
          <div className="container mx-auto px-4 max-w-5xl space-y-20">

            {/* Row 1 — NJ Law */}
            <ContentImageRow
              image={img1}
              alt="Professional brick masonry steps installation in North Haledon, NJ"
            >
              <h2 className="text-2xl font-bold mb-4 text-foreground">
                What New Jersey Law Requires From Masonry Contractors
              </h2>
              <p>
                New Jersey's Contractors Registration Act requires any contractor doing home improvement work, including masonry,{" "}
                <Link to="/paving-services/paver-patios" className="text-secondary hover:text-secondary/80 underline">patios</Link>,{" "}
                <Link to="/concrete-services/driveways" className="text-secondary hover:text-secondary/80 underline">driveways</Link>, and{" "}
                <Link to="/masonry-services/retaining-walls" className="text-secondary hover:text-secondary/80 underline">retaining walls</Link>,
                to register annually with the Division of Consumer Affairs.¹ This has been the law since 2004 and applies to any contractor working on residential properties in the state.
              </p>
              <p>
                As of January 2024, the requirements got stricter. Governor Murphy signed P.L. 2023, c. 237 into law, which created the Home Improvement and Home Elevation Contractor Licensing Act.² Under the updated rules, contractors now need to carry workers compensation insurance, maintain at least $500,000 in commercial general liability coverage, and post a compliance bond that ranges from $10,000 to $50,000 depending on the size of their contracts.³
              </p>
              <p>
                What this means for you as a homeowner is that you can verify whether a contractor is registered before you hire them. And if they are not registered, New Jersey municipalities are not allowed to issue them building permits. So if someone shows up without an NJHIC number, that is a red flag right away.
              </p>
            </ContentImageRow>

            {/* Row 2 — Insurance */}
            <ContentImageRow
              image={img2}
              alt="Retaining wall installation project in Paramus, NJ by Masonry Pro"
              reverse
            >
              <h2 className="text-2xl font-bold mb-4 text-foreground">
                Why Insurance and Bonding Matter More Than You Think
              </h2>
              <p>
                A lot of homeowners ask for quotes and compare prices without ever checking whether the contractor has insurance. This is a mistake that can get expensive fast, because if someone gets hurt on your property during the job and the contractor does not have workers comp, you could end up liable for those medical costs.
              </p>
              <p>
                New Jersey now requires home improvement contractors to carry workers compensation insurance and general liability coverage of at least $500,000 per occurrence.³ The compliance bond requirement is new as well, and it gives homeowners a layer of financial protection if the contractor fails to complete the work or does not meet their obligations.
              </p>
              <p>
                Before you sign anything, ask for a copy of the contractor's insurance certificate and check that it is current. If they hesitate or say they will get it to you later, that tells you something. A contractor who is properly insured will have no problem showing you the paperwork.
              </p>
            </ContentImageRow>

            {/* Row 3 — Training */}
            <ContentImageRow
              image={img3}
              alt="Biagio, owner of Masonry Pro Construction — over 30 years of union-trained masonry experience"
            >
              <h2 className="text-2xl font-bold mb-4 text-foreground">
                Training and Experience Are Not the Same Thing
              </h2>
              <p>
                There is a difference between a contractor who has been in business for a few years and one who actually learned the trade through formal training. Masonry is a skilled trade, and the gap between someone who was trained properly and someone who picked it up on the job can show up in the details: how mortar joints are tooled, whether the base was compacted in lifts, whether drainage was planned or skipped.
              </p>
              <p>
                Union-trained masons go through apprenticeship programs that cover materials, techniques, structural requirements, and safety standards. That kind of training matters especially in North Jersey, where the clay soils, freeze-thaw cycles, and varying municipal codes all add layers of complexity that a general handyman might not be prepared for.
              </p>
              <p>
                Biagio, the owner of{" "}
                <Link to="/about" className="text-secondary hover:text-secondary/80 underline">Masonry Pro Construction</Link>, started as a union bricklayer over 30 years ago. Gino has spent 14 years in the field learning alongside him. Between the two of them, one of them is on every job. That is not something every company can say.
              </p>
            </ContentImageRow>

            {/* Row 4 — Comparing */}
            <ContentImageRow
              image={img4}
              alt="Concrete driveway installation with proper base preparation in Kinnelon, NJ"
              reverse
            >
              <h2 className="text-2xl font-bold mb-4 text-foreground">
                What to Look for When Comparing Contractors
              </h2>
              <p>When you are getting quotes for a masonry project in North Jersey, here are some things worth paying attention to:</p>
              <ul>
                <li><strong>Written estimates with detail.</strong> A good contractor should tell you what materials they plan to use, how deep the base will be, what kind of drainage is included, and what the timeline looks like. If the estimate is vague or verbal, that usually means the plan is vague too.</li>
                <li><strong>NJ registration number.</strong> Every registered contractor has an NJHIC number that should appear on their contracts, estimates, business cards, and vehicles.¹ If you do not see it, ask for it.</li>
                <li><strong>Current insurance documentation.</strong> Ask for a certificate of insurance before any work begins. It should show workers compensation and general liability coverage at minimum.</li>
                <li><strong>Photos of completed projects in the area.</strong> Any contractor who has been doing this work in North Jersey for a while should be able to show you finished projects. Pay attention to details like clean mortar joints, level surfaces, and how the work ties into the existing property.</li>
                <li><strong>References you can actually call.</strong> Not just a list of names. Ask if you can see a completed project in person or talk to a past customer about how the process went.</li>
              </ul>
            </ContentImageRow>

            {/* Row 5 — Warning Signs */}
            <ContentImageRow
              image={img5}
              alt="Professional block work retaining wall construction in Cedar Grove, NJ"
            >
              <h2 className="text-2xl font-bold mb-4 text-foreground">
                Warning Signs That a Contractor May Cut Corners
              </h2>
              <p>
                After 30 years in this business, certain patterns show up with contractors who cut corners. If you notice any of these during the estimate or early in the process, it might be worth getting a second opinion.
              </p>
              <p>
                <strong>Asking for a large deposit up front</strong> is one. New Jersey's Home Improvement Practices regulations are clear that contractors can not demand more than a reasonable deposit, and the total contract has to be in writing for any job over $500. If someone asks for half the project cost before they start, be careful.
              </p>
              <p>
                <strong>No mention of base preparation or drainage</strong> is another. In this part of New Jersey, the soil conditions require specific base work for any{" "}
                <Link to="/paving-services/paver-patios" className="text-secondary hover:text-secondary/80 underline">paver patio</Link>,{" "}
                <Link to="/masonry-services/retaining-walls" className="text-secondary hover:text-secondary/80 underline">retaining wall</Link>, or{" "}
                <Link to="/concrete-services/patios" className="text-secondary hover:text-secondary/80 underline">concrete project</Link>. If the estimate does not include anything about excavation depth, aggregate base, or drainage, the contractor is either planning to skip it or doesn't know it is needed.
              </p>
              <p>
                <strong>No permits</strong> is another flag. A lot of masonry projects in Bergen, Passaic, Essex, and Morris Counties require zoning or building permits depending on the scope. If the contractor says permits are not needed or suggests skipping them, that puts you at risk.
              </p>
            </ContentImageRow>
          </div>
        </section>

        {/* Why North Jersey */}
        <section className="py-16 md:py-24 bg-card">
          <div className="container mx-auto px-4 max-w-3xl prose prose-lg prose-invert">
            <h2 className="text-2xl font-bold mb-4 text-foreground">
              Why the Contractor You Pick Matters More in North Jersey
            </h2>
            <p>
              The soil in this part of the state does not forgive bad base work. Clay-heavy glacial till holds water, expands when wet, and shifts through every freeze-thaw cycle. A patio that was built on a thin base with no drainage might look fine the first summer, but after a couple of North Jersey winters, the settling and shifting will start to show.
            </p>
            <p>
              Permits vary from town to town across all 39 municipalities in Morris County, and the same is true for Bergen, Passaic, and Essex. A contractor who already knows the building departments in your area can handle the permit process without delays. One who has never worked in your town may not even know what to file.
            </p>
            <p>
              Masonry is one of those trades where the quality of the work does not fully reveal itself for a few years. The contractor who built it right will not hear from you again. The one who cut corners will.
            </p>
          </div>
        </section>

        {/* FAQ Grid */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4 max-w-5xl">
            <h2 className="text-3xl font-bold text-center mb-12 text-foreground">
              Frequently Asked Questions
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {faqs.map((faq, i) => (
                <div key={i} className="bg-card rounded-xl p-6 border border-border">
                  <h3 className="text-lg font-semibold mb-3 text-foreground">{faq.q}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Citations */}
        <section className="py-12 bg-card">
          <div className="container mx-auto px-4 max-w-3xl">
            <Accordion type="single" collapsible>
              <AccordionItem value="citations" className="border-border">
                <AccordionTrigger className="text-muted-foreground hover:text-foreground text-sm">
                  Source Citations
                </AccordionTrigger>
                <AccordionContent>
                  <ol className="list-decimal list-inside space-y-2 text-muted-foreground text-sm">
                    {citations.map((c, i) => (
                      <li key={i}>{c}</li>
                    ))}
                  </ol>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </section>

        {/* Related + Back */}
        <section className="py-12">
          <div className="container mx-auto px-4 max-w-3xl">
            <div className="pt-4 border-t border-border">
              <h3 className="text-xl font-bold mb-3">Related Pro Tip</h3>
              <Link
                to="/pro-tips/benefits-of-adding-a-paver-patio"
                className="text-secondary hover:text-secondary/80 underline"
              >
                The Benefits of Adding a Paver Patio to Your Outdoor Living Space →
              </Link>
            </div>
            <div className="mt-6">
              <Link
                to="/pro-tips"
                className="text-secondary hover:text-secondary/80 font-semibold inline-flex items-center gap-2 no-underline"
              >
                ← Back to Pro Tips
              </Link>
            </div>
          </div>
        </section>

        <CTASection />
      </main>
      <Footer />
    </>
  );
}
